import { compose, constant, get, getOr, hasIn, map, memoize, pick } from 'lodash/fp';
import { createSelector, Selector } from 'reselect';
import {
    isLegacyLocalizationInitialized,
    localizeText__DO_NOT_USE,
} from '@mews-commander/legacy-app-instance/localization';
import { ApplicationState, Currency } from '@mews-commander/types';
import { createEnumOptionsSelector, getOrEmptyObject } from '@mews-commander/utils';
import { Day } from '@mews/enums';
import { getStringOrUndefinedProp, getStringProp } from '@mews/fp';
import {
    composeProps,
    createPropsExtender,
    DateTimeLocalizationInfo,
    dayNameKeys,
    dayShortNameKeys,
    monthNameKeys,
    monthShortNameKeys,
    NumberFormatting,
} from '@mews/framework';
import { globalizationSelector as moduleSelector } from '@mews/globalization';
import { arrange } from '@mews/std';
import { getCultureCode } from '../../session';

export const getLanguages = compose(getOrEmptyObject('languages'), moduleSelector);
export const getLanguageLocalNameByLanguageCode = createSelector(
    getLanguages,
    getStringProp('languageCode'),
    // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
    (languages, languageCode) => languages[languageCode]?.localName
);
export const getGlobalLegalEntityIdentifiers = compose(getOrEmptyObject('legalEntityIdentifiers'), moduleSelector);
export const getCompanyIdentifierConfigurations = compose(
    getOrEmptyObject('companyIdentifierConfigurations'),
    moduleSelector
);
export const getCountries = compose(getOrEmptyObject('countries'), moduleSelector);
export const getCultures = compose(getOrEmptyObject('cultures'), moduleSelector);
export const getCurrencies = compose(getOrEmptyObject('currencies'), moduleSelector);
export const getTexts = compose(getOrEmptyObject('texts'), moduleSelector);
export const getCultureInfos = compose(getOrEmptyObject('cultureInfos'), moduleSelector);
export const getCountrySubdivisions = compose(getOrEmptyObject('countrySubdivisions'), moduleSelector);
export const getCustomerIdentifierConfigurations = compose(
    getOrEmptyObject('customerIdentifierConfigurations'),
    moduleSelector
);
export const getLegalEnvironments = compose(getOrEmptyObject('legalEnvironments'), moduleSelector);
export const getTaxEnvironments = compose(getOrEmptyObject('taxEnvironments'), moduleSelector);
export const getTaxations = compose(getOrEmptyObject('taxations'), moduleSelector);
export const getTaxRates = compose(getOrEmptyObject('taxRates'), moduleSelector);
export const getTaxExemptionReasons = compose(getOrEmptyObject('taxExemptionReasons'), moduleSelector);
const getAccommodationEnvironments = compose(getOrEmptyObject('accommodationEnvironments'), moduleSelector);
export const getAccommodationEnvironment = createSelector(
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    getStringOrUndefinedProp('code'),
    getAccommodationEnvironments,
    get
);
export const getTimeZones = compose(getOr([], 'timeZones'), moduleSelector);

export const getCurrentDateTimeCultureInfo = createSelector(
    () => getCultureCode(),
    getCultureInfos,
    (cultureCode, cultureInfos) => get([cultureCode, 'dateTime'])(cultureInfos)
);

export const getCurrencyLocalizationInfo = createSelector(
    getCurrencies,
    getStringProp('cultureCode'),
    getStringProp('currencyCode'),
    (
        currencies,
        cultureCode,
        currencyCode
    ): Pick<Currency, 'symbol' | 'symbolPosition' | 'symbolSpacing' | 'precision'> => {
        let currency = get(currencyCode, currencies);

        if (hasIn(['cultureFormats', cultureCode], currency)) {
            const cultureFormat = get(['cultureFormats', cultureCode], currency);
            currency = { ...currency, ...cultureFormat };
        }

        return pick(['symbol', 'symbolPosition', 'symbolSpacing', 'precision'], currency);
    }
);

export const getCurrencyCultureInfo = createSelector(
    getCultureInfos,
    getStringProp('cultureCode'),
    (cultureInfos, cultureCode) => get([cultureCode, 'currencyValue'])(cultureInfos)
);

export const getNumberCultureInfo = createSelector(
    getCultureInfos,
    getStringProp('cultureCode'),
    (cultureInfos, cultureCode) => get([cultureCode, 'number'])(cultureInfos)
);

export const getCurrentNumberCultureInfo = composeProps(
    getNumberCultureInfo,
    createPropsExtender('cultureCode', () => getCultureCode())
) as Selector<ApplicationState, NumberFormatting>;

export const getCurrentCurrencyCultureInfo = composeProps(
    getCurrencyCultureInfo,
    createPropsExtender('cultureCode', () => getCultureCode())
) as Selector<ApplicationState, NumberFormatting>;

const memoizeWithResolver = (memoize as $TSFixMe).convert({ fixed: false });

const localizeNames = (keys: Mews.TextKey[]): (() => string[]) =>
    memoizeWithResolver(
        () => keys.map((k) => (isLegacyLocalizationInitialized() ? localizeText__DO_NOT_USE({ textKey: k }) : '')),
        () => isLegacyLocalizationInitialized()
    );

export const getMonthNames = localizeNames(monthNameKeys);
export const getMonthShortNames = localizeNames(monthShortNameKeys);
export const getDayNames = localizeNames(dayNameKeys);
export const getDayShortNames = localizeNames(dayShortNameKeys);

export const getCurrentDateTimeLocalizationInfo = createSelector(
    () => getCultureCode(),
    getCurrentDateTimeCultureInfo,
    getMonthNames,
    getMonthShortNames,
    getDayNames,
    getDayShortNames,
    (cultureCode, cultureInfo, monthNames, monthShortNames, dayNames, dayShortNames) => ({
        cultureCode,
        ...cultureInfo,
        monthNames,
        monthShortNames,
        dayNames,
        dayShortNames,
    })
) as Selector<ApplicationState, DateTimeLocalizationInfo>;

const getCurrentLocalizationWeekStart = createSelector(
    getCurrentDateTimeLocalizationInfo,
    ({ weekStart }) => Day.getByName(weekStart).value
);

const createCultureOrderedDayElementsSelector = <T>(dayElementsSelector: (state: ApplicationState) => T[]) =>
    createSelector(dayElementsSelector, getCurrentLocalizationWeekStart, arrange);

export const getCultureOrderedDayEnumValues = createCultureOrderedDayElementsSelector(constant(Day.enumValues));

export const getCultureOrderedDayEnumNames = createSelector(getCultureOrderedDayEnumValues, map('name'));

export const getCultureOrderedDayNames = createCultureOrderedDayElementsSelector(getDayNames);

export const getCultureOrderedDayShortNames = createCultureOrderedDayElementsSelector(getDayShortNames);

export const getDayOptions = createEnumOptionsSelector(getCultureOrderedDayEnumValues, { useNameAsValue: true });
